import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';
import { AlertCircle, CheckCircle2, ArrowRight } from 'lucide-react';
interface ResultScreenProps {
  onContinue: () => void;
}
export function ResultScreen({
  onContinue
}: ResultScreenProps) {
  const [score, setScore] = useState(0);
  const targetScore = 37;
  useEffect(() => {
    const duration = 1500;
    const steps = 60;
    const stepTime = Math.abs(Math.floor(duration / steps));
    let current = 0;
    const timer = setInterval(() => {
      current += targetScore / steps;
      if (current >= targetScore) {
        setScore(targetScore);
        clearInterval(timer);
      } else {
        setScore(Math.floor(current));
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45
      }
    }
  };
  return <div className="flex flex-col min-h-screen bg-gray-50 pb-8" data-id="element-94">
      {/* Header Section */}
      <motion.div initial={{
      opacity: 0,
      y: -16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="bg-white pt-8 pb-6 px-5 rounded-b-[2rem] shadow-sm mb-6" data-id="element-95">
        <h1 className="text-result-h1 md:text-result-h1-desktop font-heading font-bold text-brand-dark mb-6 text-center" data-id="element-96">
          Tu resultado muestra un perfil de alta intensidad emocional y
          dificultad con rutinas
        </h1>

        <div className="bg-brand-beige/30 rounded-2xl p-5 mb-5 border border-brand-beige/50 relative overflow-hidden" data-id="element-97">
          <div className="absolute top-0 right-0 w-28 h-28 bg-white/40 rounded-full blur-2xl -mr-8 -mt-8" data-id="element-98" />
          <p className="text-disclaimer text-gray-500 font-bold mb-1.5 uppercase tracking-wider relative z-10 text-center" data-id="element-99">
            Nivel de estrés familiar
          </p>
          <div className="flex items-end justify-center space-x-2 relative z-10" data-id="element-100">
            <span className="text-result-score font-heading font-bold text-brand-dark" data-id="element-101">
              {score}
            </span>
            <span className="text-result-h2 text-gray-400 font-medium mb-1" data-id="element-102">
              / 50
            </span>
          </div>
          <div className="w-full h-2.5 bg-white/60 rounded-full mt-4 overflow-hidden relative z-10" data-id="element-103">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '74%'
          }} transition={{
            duration: 1.5,
            delay: 0.5,
            ease: 'easeOut'
          }} className="h-full bg-orange-400 rounded-full shadow-[0_0_10px_rgba(251,146,60,0.5)]" data-id="element-104" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-5 border-l-4 border-l-brand-blue shadow-sm border-y border-r border-gray-100" data-id="element-105">
          <p className="text-disclaimer text-brand-blue font-bold mb-1.5 uppercase tracking-wider" data-id="element-106">
            Perfil dominante
          </p>
          <p className="text-result-h2 font-heading font-bold text-brand-dark mb-3" data-id="element-107">
            Niño intenso con alta reactividad y necesidad de estructura simple
          </p>
          <div className="inline-flex items-center space-x-2 bg-blue-50 px-3 py-1.5 rounded-xl" data-id="element-108">
            <div className="w-2 h-2 rounded-full bg-brand-blue animate-pulse" data-id="element-109" />
            <span className="text-disclaimer font-bold text-brand-blue" data-id="element-110">
              91% de afinidad con este patrón
            </span>
          </div>
        </div>
      </motion.div>

      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="px-5 space-y-5" data-id="element-111">
        {/* Disclaimer */}
        <motion.div variants={itemVariants} className="flex items-start space-x-3 bg-white p-3.5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-112">
          <AlertCircle className="text-gray-400 shrink-0 mt-0.5" size={18} data-id="element-113" />
          <p className="text-disclaimer text-gray-500 leading-relaxed" data-id="element-114">
            Esto no es un diagnóstico médico. Es una orientación educativa
            basada en tus respuestas para ayudarte a identificar qué tipo de
            apoyo puede necesitar tu hijo en casa.
          </p>
        </motion.div>

        {/* Explanation */}
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-115">
          <h2 className="text-result-h2 font-heading font-bold text-brand-dark mb-3 text-center" data-id="element-116">
            ¿Qué significa esto?
          </h2>
          <p className="text-gray-600 mb-6 leading-relaxed text-result-body text-center" data-id="element-117">
            Tu hijo puede reaccionar rápido ante el estrés, frustrarse con
            transiciones, resistirse a instrucciones o buscar pantallas como una
            forma de calmarse. Esto no significa que esté "mal" ni que tú seas
            mal padre o mala madre. Significa que tu familia necesita
            herramientas más simples, visibles y repetibles.
          </p>

          <ul className="space-y-4" data-id="element-118">
            {['Puede necesitar instrucciones más cortas y visuales', 'Puede frustrarse cuando hay demasiados pasos', 'Puede reaccionar fuerte cuando se siente sobreestimulado', 'Puede necesitar rutinas predecibles para sentirse seguro', 'Tú puedes necesitar un plan claro para no responder desde el cansancio'].map((bullet, i) => <motion.li key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5 + i * 0.08
          }} className="flex items-start space-x-3" data-id="element-119">
                <CheckCircle2 className="text-brand-green shrink-0 mt-0.5" size={20} data-id="element-120" />
                <span className="text-gray-700 font-medium text-result-body" data-id="element-121">
                  {bullet}
                </span>
              </motion.li>)}
          </ul>
        </motion.div>

        {/* Emotional Bridge */}
        <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-122">
          <h2 className="text-result-h2 font-heading font-bold text-brand-dark mb-4 text-center" data-id="element-123">
            La buena noticia: existe un camino más simple.
          </h2>
          <p className="text-gray-600 leading-relaxed text-result-body text-center mb-5" data-id="element-124">
            No necesitas leer libros enteros ni convertirte en experto en
            crianza. Necesitas un plan claro que te diga qué hacer hoy, paso a
            paso.
          </p>
          <div className="bg-brand-beige/30 p-4 rounded-2xl border border-brand-beige/50" data-id="element-125">
            <p className="text-gray-700 leading-relaxed text-center font-medium text-result-body" data-id="element-126">
              Un sistema simple, repetible, pensado para padres reales que
              quieren más calma sin sentirse perdidos.
            </p>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="pb-safe" data-id="element-127">
          <Button variant="success" onClick={onContinue} className="group shadow-xl shadow-green-200/50" data-id="element-128">
            Ver mi plan recomendado
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} data-id="element-129" />
          </Button>
        </motion.div>
      </motion.div>
    </div>;
}